import { Fragment } from 'react';
import type { FeedItem } from '../../domain/leaderboard/feed';
import type { SocialAward } from '../../domain/awards/types';
import { MonthRecapCard } from '../awards/MonthRecapCard';
import { CATEGORY_LABELS } from '../../domain/scoring/categories';
import { formatLoadAndReps } from '../../domain/workouts/describeLift';
import { groupByDay, formatDayHeading } from '../../domain/workouts/dayGroups';
import '../../components/DayGroup/DayGroup.css';

export interface FeedListProps {
  items: FeedItem[];
  currentUserId?: string;
  /** How many to render; the rest stay loaded behind "Show more". */
  visibleCount: number;
  onShowMore: () => void;
  onSelectUser: (userId: string, displayName: string) => void;
  /** Finished months' results, keyed by month, to head that month's lifts. */
  awardsByMonth?: Record<string, SocialAward[]>;
  onSeeMonth?: (monthKey: string) => void;
}

export function FeedList({
  items,
  currentUserId,
  visibleCount,
  onShowMore,
  onSelectUser,
  awardsByMonth,
  onSeeMonth,
}: FeedListProps) {
  if (items.length === 0) {
    return <div className="leaderboard__empty">No lifts logged yet. Be the first.</div>;
  }

  const visible = items.slice(0, visibleCount);
  // The items already carry their workout's date, so the shared grouping used by
  // the history list works directly - and the two look identical as a result.
  const days = groupByDay(
    visible.map((item) => ({ ...item, workoutDate: item.workout.workoutDate })),
  );

  // A month's recap heads that month's lifts. Walking newest-first, the first
  // day belonging to a month is where the month starts, so the card goes there
  // - which puts it exactly at the boundary the reader just crossed. Worked out
  // up front rather than tracked through the map, so rendering stays pure.
  const monthStarts = new Map<string, string>();
  for (const day of days) {
    const month = day.date.slice(0, 7);
    if (!monthStarts.has(month)) monthStarts.set(month, day.date);
  }

  return (
    <div className="feed">
      <div className="day-groups">
        {days.map((day) => {
          const month = day.date.slice(0, 7);
          const recap = monthStarts.get(month) === day.date ? awardsByMonth?.[month] : undefined;

          return (
            <Fragment key={day.date}>
              {recap && recap.length > 0 && (
                <MonthRecapCard
                  monthKey={month}
                  awards={recap}
                  onSeeAll={() => onSeeMonth?.(month)}
                />
              )}
              <section className="day-group">
                <h4 className="day-group__label">{formatDayHeading(day.date)}</h4>
                <ul className="day-group__items">
                  {day.items.map(({ workout, displayName, badges, rankMovement }) => (
                    <li key={workout.id}>
                      <button
                        type="button"
                        className={`feed__item ${workout.userId === currentUserId ? 'feed__item--mine' : ''}`}
                        onClick={() => onSelectUser(workout.userId, displayName)}
                        aria-label={`View ${displayName}'s history`}
                      >
                        <div className="feed__head">
                          <span className="feed__who-group">
                            <span className="feed__who">{displayName}</span>
                            {rankMovement && (
                              <span
                                className="feed__rank"
                                title={`Moved up to #${rankMovement.to} from #${rankMovement.from}`}
                              >
                                <span aria-hidden="true">▲</span> #{rankMovement.to}
                              </span>
                            )}
                          </span>
                          <span className="feed__points">{Math.round(workout.points)} pts</span>
                        </div>

                        <div className="feed__meta">
                          <span className="feed__exercise">
                            {workout.exerciseNameSnapshot}
                            <span className="feed__load"> · {formatLoadAndReps(workout)}</span>
                          </span>
                          <span className="feed__category">
                            {CATEGORY_LABELS[workout.category]}
                          </span>
                        </div>

                        {/* Each badge carries its own word, so colour is never the only cue. */}
                        {(badges.isCategoryPr ||
                          badges.isExercisePr ||
                          badges.isCategoryMonthBest) && (
                          <div className="feed__badges">
                            {badges.isCategoryPr && (
                              <span className="feed__badge feed__badge--category-pr">
                                <span aria-hidden="true">★</span>{' '}
                                {CATEGORY_LABELS[workout.category]} PR
                              </span>
                            )}
                            {badges.isExercisePr && (
                              <span className="feed__badge feed__badge--lift-pr">
                                <span aria-hidden="true">▲</span> Lift PR
                              </span>
                            )}
                            {badges.isCategoryMonthBest && (
                              <span className="feed__badge feed__badge--month">
                                <span aria-hidden="true">●</span>{' '}
                                {CATEGORY_LABELS[workout.category]} MB
                              </span>
                            )}
                          </div>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            </Fragment>
          );
        })}
      </div>

      {items.length > visibleCount && (
        <button type="button" className="feed__more" onClick={onShowMore}>
          Show more ({items.length - visibleCount} older)
        </button>
      )}
    </div>
  );
}
